/**
 * The CRM's prohibited wording list (GET /v1/chats/prohibited-terms), matched
 * while typing like the CRM chat window's prohibited-terms.js. The CRM still
 * refuses a send with flagged words until it is confirmed; this only warns early.
 */
import { useEffect, useMemo, useState } from 'react';
import { crmFetch, dataOf } from './api';

interface Pattern {
  term: string;
  regex: RegExp;
}

const escape = (v: string) => v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export function compileTerms(terms: unknown): Pattern[] {
  const seen = new Set<string>();
  return (Array.isArray(terms) ? terms : [])
    .map((t) => String(t || '').trim())
    .filter((t) => {
      const key = t.toLocaleLowerCase('ru');
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => b.length - a.length || a.localeCompare(b))
    .map((term) => ({
      term,
      regex: new RegExp(`(^|[^\\p{L}\\p{N}_])(${escape(term).replace(/\s+/g, '[\\s._-]+')})(?=$|[^\\p{L}\\p{N}_])`, 'iu'),
    }));
}

export function findTerms(text: string, patterns: Pattern[]): string[] {
  if (!text.trim()) return [];
  return patterns.filter((p) => p.regex.test(text)).map((p) => p.term);
}

let loaded: Promise<Pattern[]> | null = null;

function loadPatterns(accountId: string): Promise<Pattern[]> {
  if (!loaded) {
    loaded = crmFetch('/v1/chats/prohibited-terms', { accountId, local: true })
      .then((body) => compileTerms(dataOf<{ terms?: unknown }>(body).terms))
      .catch(() => {
        loaded = null;
        return [];
      });
  }
  return loaded;
}

/** Flagged terms in `text` (empty until the list has loaded). */
export function useProhibitedTerms(accountId: string, text: string): string[] {
  const [patterns, setPatterns] = useState<Pattern[]>([]);
  useEffect(() => {
    let live = true;
    void loadPatterns(accountId).then((p) => live && setPatterns(p));
    return () => {
      live = false;
    };
  }, [accountId]);
  return useMemo(() => findTerms(text, patterns), [text, patterns]);
}
